import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { MaskedLine, Reveal, FadeIn } from "@/components/Reveal";
import EclipseRing from "@/components/EclipseRing";
import { IMAGES, VALUES, METHOD_STUDIO } from "@/data/content";

const ZONES = ["Gard", "Drôme", "Ardèche", "Vaucluse"];

const Studio = () => (
  <div data-testid="studio-page">
    {/* ————— HERO ————— */}
    <section className="relative overflow-hidden px-6 pb-24 pt-44 sm:px-10 sm:pb-32 sm:pt-56">
      <div className="pointer-events-none absolute -right-32 top-16 opacity-50">
        <EclipseRing size={480} />
      </div>
      <div className="relative mx-auto max-w-6xl">
        <FadeIn delay={0.2}>
          <p className="overline-label">Le Studio</p>
        </FadeIn>
        <h1 className="mt-10 font-serif font-light leading-[1.02] tracking-tight text-ivoire">
          <MaskedLine delay={0.4} className="text-4xl sm:text-6xl lg:text-7xl">
            Chaque entreprise a une histoire.
          </MaskedLine>
          <MaskedLine delay={0.58} className="text-4xl sm:text-6xl lg:text-7xl">
            <em className="italic text-champagne">Son image doit être à sa hauteur.</em>
          </MaskedLine>
        </h1>
      </div>
    </section>

    {/* ————— VISION ————— */}
    <section className="mx-auto max-w-6xl px-6 py-24 sm:px-10 sm:py-32">
      <div className="grid items-center gap-16 md:grid-cols-12">
        <div className="md:col-span-6">
          <Reveal>
            <p className="overline-label">Notre vision</p>
            <h2 className="mt-8 font-serif text-3xl font-light leading-tight tracking-tight text-ivoire sm:text-5xl">
              Traverser le temps, <em className="italic text-champagne">pas les modes.</em>
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-10 text-base leading-relaxed text-ivoire/65">
              Chez Rémanence Studio, nous sommes convaincus qu'une identité visuelle
              ne doit pas suivre les tendances. Elle doit traverser le temps.
            </p>
            <p className="mt-6 text-base leading-relaxed text-ivoire/65">
              Nous créons des univers graphiques sobres, élégants et cohérents qui
              valorisent votre savoir-faire et inspirent confiance dès le premier
              regard. Parce qu'une image forte ne sert pas seulement à être
              remarquée : elle permet d'être reconnue, comprise et mémorisée.
            </p>
          </Reveal>
        </div>
        <Reveal delay={0.15} className="md:col-span-5 md:col-start-8">
          <div className="group relative">
            <div className="absolute -inset-4 rounded-full border border-champagne/15" aria-hidden="true" />
            <div className="overflow-hidden border border-white/8">
              <img
                src={IMAGES.studio}
                alt="Univers du studio"
                loading="lazy"
                className="img-luxe aspect-[4/5] w-full object-cover"
              />
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    {/* ————— PHILOSOPHIE ————— */}
    <section className="relative overflow-hidden border-y border-white/5 bg-charbon/40 py-28 sm:py-40">
      <div className="mx-auto max-w-4xl px-6 text-center sm:px-10">
        <Reveal>
          <p className="overline-label">Notre philosophie</p>
          <h2 className="mt-10 font-serif text-5xl font-light tracking-tight text-ivoire sm:text-7xl">
            Créer ce qui <em className="italic text-champagne">demeure.</em>
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="gold-hairline mx-auto mt-12 w-40" />
          <p className="mt-12 text-base leading-relaxed text-ivoire/65 sm:text-lg">
            Le nom Rémanence évoque la trace qui reste après une expérience. C'est
            cette idée qui guide notre travail : concevoir des identités visuelles
            qui marquent les esprits et accompagnent durablement le développement
            des entreprises.
          </p>
          <p className="mt-6 text-base leading-relaxed text-ivoire/65 sm:text-lg">
            Nous privilégions la simplicité, la précision et l'intemporalité plutôt
            que les effets de mode.
          </p>
        </Reveal>
      </div>
    </section>

    {/* ————— À PROPOS ————— */}
    <section className="mx-auto max-w-6xl px-6 py-24 sm:px-10 sm:py-32">
      <div className="grid items-center gap-16 md:grid-cols-12">
        <Reveal className="order-2 md:order-1 md:col-span-5">
          <div className="overflow-hidden border border-white/8">
            <img
              src={IMAGES.portrait}
              alt="Ronald Bellaiche, fondateur de Rémanence Studio"
              loading="lazy"
              className="img-luxe aspect-[3/4] w-full object-cover"
            />
          </div>
        </Reveal>
        <div className="order-1 md:order-2 md:col-span-6 md:col-start-7">
          <Reveal>
            <p className="overline-label">À propos</p>
            <h2 className="mt-8 font-serif text-3xl font-light tracking-tight text-ivoire sm:text-4xl">
              Ronald Bellaiche, <em className="italic text-champagne">fondateur</em>
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-10 text-base leading-relaxed text-ivoire/65">
              J'ai créé Rémanence Studio avec une conviction simple : une
              communication réussie repose autant sur l'écoute que sur le design.
            </p>
            <p className="mt-6 text-base leading-relaxed text-ivoire/65">
              Chaque projet débute par une compréhension de votre activité, de vos
              objectifs et de votre personnalité. Cette approche nous permet de
              créer des supports qui vous ressemblent et qui parlent réellement à
              vos clients.
            </p>
            <p className="mt-6 text-base leading-relaxed text-ivoire/65">
              Je travaille principalement avec des artisans, commerçants,
              professions libérales, associations et PME souhaitant renforcer leur
              image avec des outils professionnels, durables et cohérents.
            </p>
          </Reveal>
        </div>
      </div>
    </section>

    {/* ————— VALEURS ————— */}
    <section className="border-y border-white/5 bg-charbon/40 py-24 sm:py-32">
      <div className="mx-auto max-w-6xl px-6 sm:px-10">
        <Reveal className="mb-16">
          <p className="overline-label">Nos valeurs</p>
          <h2 className="mt-6 font-serif text-3xl font-light tracking-tight text-ivoire sm:text-5xl">
            Ce qui guide chaque création
          </h2>
        </Reveal>
        <div className="grid gap-6 sm:grid-cols-2">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.08}>
              <div
                data-testid={`value-card-${i}`}
                className="group h-full border border-white/8 bg-noir p-10 transition-[border-color,transform] duration-500 hover:-translate-y-1 hover:border-champagne/40 sm:p-12"
              >
                <span className="font-serif text-sm italic text-champagne/70">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="mt-6 font-serif text-2xl font-light text-ivoire transition-colors duration-400 group-hover:text-champagne sm:text-3xl">
                  {v.title}
                </h3>
                <p className="mt-4 text-base leading-relaxed text-ivoire/60">{v.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* ————— MÉTHODE ————— */}
    <section className="mx-auto max-w-6xl px-6 py-24 sm:px-10 sm:py-32">
      <Reveal className="mb-20 text-center">
        <p className="overline-label">Notre méthode</p>
        <h2 className="mt-6 font-serif text-3xl font-light tracking-tight text-ivoire sm:text-5xl">
          Quatre temps, une exigence
        </h2>
      </Reveal>
      <div className="relative mx-auto max-w-2xl">
        <div className="absolute bottom-8 left-[13px] top-8 w-px bg-gradient-to-b from-champagne/50 via-white/10 to-transparent" aria-hidden="true" />
        {METHOD_STUDIO.map((step, i) => (
          <Reveal key={step.num} delay={i * 0.08}>
            <div data-testid={`method-step-${i}`} className="relative flex gap-10 pb-16 last:pb-0">
              <span className="relative z-10 mt-1 block h-[27px] w-[27px] shrink-0 rounded-full border border-champagne/60 bg-noir" />
              <div>
                <p className="font-serif text-sm italic text-champagne">{step.num}.</p>
                <h3 className="mt-2 font-serif text-2xl font-light text-ivoire sm:text-3xl">{step.title}</h3>
                <p className="mt-3 max-w-md text-base leading-relaxed text-ivoire/60">{step.text}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    {/* ————— ZONE ————— */}
    <section className="border-t border-white/5 py-24 sm:py-32">
      <div className="mx-auto max-w-6xl px-6 sm:px-10">
        <div className="grid gap-14 md:grid-cols-12">
          <Reveal className="md:col-span-5">
            <p className="overline-label">Zone d'intervention</p>
            <h2 className="mt-6 font-serif text-3xl font-light leading-tight tracking-tight text-ivoire sm:text-4xl">
              Basé à Monteils, <em className="italic text-champagne">dans le Gard.</em>
            </h2>
            <p className="mt-8 text-base leading-relaxed text-ivoire/60">
              Rémanence Studio accompagne les professionnels du Sud de la France.
              Les projets à distance sont également possibles partout en France.
            </p>
          </Reveal>
          <Reveal delay={0.12} className="md:col-span-6 md:col-start-7">
            <div className="flex flex-wrap gap-4">
              {ZONES.map((z) => (
                <span
                  key={z}
                  data-testid={`zone-chip-${z.toLowerCase()}`}
                  className="rounded-full border border-ivoire/20 px-7 py-3 font-serif text-xl font-light text-ivoire/80 transition-[border-color,color] duration-400 hover:border-champagne/60 hover:text-champagne"
                >
                  {z}
                </span>
              ))}
              <span className="rounded-full border border-champagne/40 px-7 py-3 font-serif text-xl font-light italic text-champagne">
                … et partout en France, à distance
              </span>
            </div>
          </Reveal>
        </div>

        <Reveal className="mt-28 text-center">
          <p className="mx-auto max-w-2xl font-serif text-2xl font-light leading-relaxed text-ivoire/80 sm:text-3xl">
            Une belle identité attire le regard. Une identité cohérente inspire
            confiance. <em className="italic text-champagne">Une identité durable devient un véritable outil de développement.</em>
          </p>
          <Link
            to="/services"
            data-testid="studio-cta-services"
            className="group mt-12 inline-flex items-center gap-3 rounded-full bg-champagne px-10 py-4 text-[13px] uppercase tracking-[0.2em] text-noir transition-[background-color] duration-400 hover:bg-ivoire"
          >
            Découvrir nos services
            <ArrowRight size={15} strokeWidth={1.5} className="transition-transform duration-400 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </section>
  </div>
);

export default Studio;
